import { ApiClient, TokenAdd } from "@/services/apiClient";
import { afterLogin } from "@/services/user/auth/login/afterLoginService";
import { setupUserInApp } from "@/services/user/auth/login/helpers";
import { TokenService } from "@/services/user/auth/login/tokenService";
import type { User } from "@/services/user/userTypes";
import { AuthenticationException, TokenNotValidException } from "@/services/user/userExceptions";
import { AxiosError } from "axios";
import { useUser } from "@/composables/user/user";

export type AuthenticationPromise = Promise<void> | null;

type Credentials = { email: string; password: string } | { token: string };

const isInProgressOrDone = (): boolean => {
  return useUser().authPromise.value !== null;
};

const run = async (credentials: Credentials): Promise<AuthenticationPromise> => {
  const { authPromise } = useUser();
  if (!isInProgressOrDone()) {
    authPromise.value = createAuthenticationPromise(credentials);
  }
  return authPromise.value;
};

const reset = (): void => {
  useUser().authPromise.value = null;
};

const resolveManually = () => {
  useUser().authPromise.value = Promise.resolve();
};

const createAuthenticationPromise = (credentials: Credentials): AuthenticationPromise => {
  const authUrl = "user/authenticate";
  return ApiClient.postPublic<User>(authUrl, {}, credentials, TokenAdd.Never)
    .then(({ data: user }) => setupUserInApp(user))
    .then(afterLogin)
    .catch(handleAuthenticationError);
};

const handleAuthenticationError = (error: unknown): void => {
  TokenService.delete();

  if (error instanceof AxiosError && error.response?.status === 400) {
    const errorCode = error.response?.data.status;
    const TokenNotValidStatusCode = 3;
    if (errorCode === TokenNotValidStatusCode) {
      throw new TokenNotValidException();
    } else {
      const msg = error.response?.data.message;
      throw new AuthenticationException(msg);
    }
  } else {
    throw error;
  }
};

export const Authentication = {
  get promise() {
    return useUser().authPromise.value;
  },
  isInProgressOrDone,
  run,
  reset,
  resolveManually,
};
